import axios, { AxiosError } from "axios";
import { SecureStorage } from "../../utils/secure-storage";
import { config } from "../../config/env";
import { logger, captureException } from "../../utils/logger";
import { parseApiError } from "../../utils/api-error";
import { logAuditEvent } from "../../utils/audit-log";
import { redirectToLoginPreservingLocation } from "../../utils/return-url";

const baseURL = config.API_BASE_URL;

export const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: config.API_TIMEOUT,
});

// Request interceptor (async для Secure Storage)
api.interceptors.request.use(async (requestConfig) => {
  const token = await SecureStorage.get("accessToken");
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  logger.debug("HTTP request", {
    url: requestConfig.url,
    method: requestConfig.method,
    params: requestConfig.params,
  });
  return requestConfig;
});

// Response interceptor для обработки ошибок
api.interceptors.response.use(
  (response) => {
    logger.debug("HTTP response", {
      url: response.config?.url,
      status: response.status,
    });
    return response;
  },
  async (error: AxiosError) => {
    const apiError = parseApiError(error);

    // КРИТИЧНО: Обработка rate limiting (429 Too Many Requests)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers["retry-after"];
      const retryAfterSeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

      // КРИТИЧНО: Audit logging для rate limiting (SOC 2 compliance)
      logAuditEvent({
        action: "rate_limit.exceeded",
        resource: error.config?.url || "unknown",
        status: "failure", // blocked не является валидным статусом, используем 'failure'
        details: { retryAfter: retryAfterSeconds },
      }).catch(() => {
        // Не критично, если audit log не отправился
      });

      // Показать пользователю понятное сообщение через toast
      // Используем динамический импорт для избежания циклических зависимостей
      import("../../hooks/useNotifications")
        .then(() => {
          // Toast будет показан в компоненте, который обрабатывает ошибку
          // Здесь только логируем для debugging
          logger.warn("Rate limit exceeded", {
            url: error.config?.url,
            retryAfter: retryAfterSeconds,
          });
        })
        .catch(() => {
          // Если не удалось импортировать, просто логируем
          logger.warn("Rate limit exceeded", {
            url: error.config?.url,
            retryAfter: retryAfterSeconds,
          });
        });

      // ВАЖНО: НЕ делаем автоматический retry для 429
      // Это может усугубить ситуацию и увеличить время блокировки
      // Пробрасываем ошибку дальше для обработки в компонентах
    }

    // Обработка 401 - автоматический redirect на страницу входа. С публичных
    // страниц хелпер не уводит (бутстрап-401 гостя — штатно), с защищённых —
    // сохраняет место в sessionStorage.returnUrl (SSOT return-url.ts).
    if (error.response?.status === 401) {
      await SecureStorage.remove("accessToken");
      await SecureStorage.remove("refreshToken");
      redirectToLoginPreservingLocation();
    }

    // Логирование для debugging (только в dev mode)
    if (process.env["NODE_ENV"] === "development") {
      console.error("API Error:", {
        url: error.config?.url,
        method: error.config?.method,
        status: apiError.statusCode,
        message: apiError.message,
        // КРИТИЧНО: НЕ логировать sensitive fields
        // details: apiError.details, // Может содержать пароли, токены, PII
      });
    }

    // Логирование через logger
    captureException(error, {
      code: apiError.statusCode,
      url: error.config?.url,
    });

    // КРИТИЧНО: Пробрасываем ошибку дальше для обработки в компонентах
    return Promise.reject(error);
  },
);

// Экспорт для совместимости со старыми файлами
export const httpClient = api;

export default api;
